import type { ReportIssuePossibleDuplicate } from '../../../../api/DigiobcanApi';
import { getReportStatusMeta } from '../../../../components/AdminReportStatusItem';
import { __ } from '../../../../utils';
import DecisionChip from './DecisionChip';
import type { DuplicateDirection } from './issueDecision';
import CheckIcon from '@mui/icons-material/Check';
import {
  Box,
  Stack,
  StatusChip,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';

type Props = {
  candidates: ReportIssuePossibleDuplicate[];
  direction: DuplicateDirection;
  picked: string[];
  onDirectionChange: (direction: DuplicateDirection) => void;
  onPickedChange: (picked: string[]) => void;
  compact?: boolean;
};

/**
 * Mobil dostáva kratšie znenie tej istej voľby - nie inú voľbu. Na 390 px sa
 * dlhý popis zalomí na tri riadky a z troch volieb je stĺpec vysoký ako pol
 * obrazovky.
 */
const DIRECTIONS: {
  value: DuplicateDirection;
  label: string;
  shortLabel: string;
}[] = [
  {
    value: 'child',
    label: __('Tento nález je duplikát iného'),
    shortLabel: __('Je duplikát iného'),
  },
  {
    value: 'parent',
    label: __('Tento nález je originál, ostatné sú duplikáty'),
    shortLabel: __('Je originál'),
  },
  {
    value: 'none',
    label: __('Nie sú to duplikáty'),
    shortLabel: __('Nie sú to duplikáty'),
  },
];

/** „12 m · zhoda …" - bez percenta, to API zámerne nedopočítava. */
const candidateMeta = (candidate: ReportIssuePossibleDuplicate) => {
  const bits = [`${__('podnet')} ${candidate.report_id}`];
  if (candidate.distance_meters !== undefined) {
    bits.push(`${candidate.distance_meters} m`);
  }
  return bits.join(' · ');
};

/**
 * Stavy, v ktorých sa nález ešte môže stať duplikátom iného. Zrkadlí
 * `DUPLICABLE_STATUSES` v API: vyriešený nález je uzavretý finálnym
 * stanoviskom a to sa občanovi neberie, presunutý ani už zlúčený nález
 * v kategórii nežije.
 */
const DUPLICABLE_STATUSES: ReportIssuePossibleDuplicate['status'][] = [
  'open',
  'accepted',
];

/**
 * Prečo sa kandidát vybrať nedá - alebo `undefined`, keď sa dá.
 *
 * Pýta sa len smer `parent`, kde kandidáti vystupujú ako **duplikáty**;
 * originálu API žiadnu podmienku stavu nekladie. Kým to tu nebolo, dal sa
 * vyriešený nález pokojne naklikať a chyba prišla až z API, po odoslaní
 * celého rozhodnutia.
 */
const blockedReason = (
  candidate: ReportIssuePossibleDuplicate,
  direction: DuplicateDirection,
) => {
  if (direction !== 'parent' || DUPLICABLE_STATUSES.includes(candidate.status))
    return undefined;

  if (candidate.status === 'resolved')
    return __('nedá sa zlúčiť - nález je už vyriešený');
  if (candidate.status === 'duplicate')
    return __('nedá sa zlúčiť - nález už je duplikát iného');
  return __('nedá sa zlúčiť - nález je presunutý inam');
};

/**
 * Smer duplicity a výber kandidátov.
 *
 * Smer určuje úradník, systém len navrhuje - preto sú to tri rovnocenné chipy
 * a nie „zlúčiť" tlačidlo. Zmena smeru vyčistí výber: pri `child` sa vyberá
 * jeden originál, pri `parent` viacero duplikátov, takže prenášať výber medzi
 * nimi by znamenalo ticho zmeniť význam toho, čo už úradník naklikal.
 */
const IssueDuplicates: React.FC<Props> = ({
  candidates,
  direction,
  picked,
  onDirectionChange,
  onPickedChange,
  compact = false,
}) => {
  const single = direction === 'child';

  const toggle = (id: string, blocked: boolean) => {
    if (blocked) return;
    if (single) {
      onPickedChange(picked[0] === id ? [] : [id]);
      return;
    }
    onPickedChange(
      picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id],
    );
  };

  return (
    <Stack gap={1.25}>
      <Stack direction='row' alignItems='center' gap={1} flexWrap='wrap'>
        <Typography variant='body2' fontWeight={500}>
          {__('Možné duplikáty')}
        </Typography>
        <Typography
          variant='caption'
          sx={{
            px: 0.875,
            py: 0.25,
            borderRadius: 0.5,
            backgroundColor: 'info.surface',
            color: 'info.dark',
          }}
        >
          {`${__('systém našiel')} ${candidates.length}`}
        </Typography>
      </Stack>

      <Stack
        direction={compact ? 'column' : 'row'}
        gap={compact ? 0.75 : 1}
        flexWrap={compact ? 'nowrap' : 'wrap'}
      >
        {DIRECTIONS.map((option) => (
          <DecisionChip
            key={option.value}
            selected={direction === option.value}
            fullWidth={compact}
            onClick={() => {
              onDirectionChange(option.value);
              onPickedChange([]);
            }}
          >
            {compact ? option.shortLabel : option.label}
          </DecisionChip>
        ))}
      </Stack>

      {direction !== 'none' && (
        <Stack
          gap={0.5}
          sx={{
            p: 0.75,
            borderRadius: 1,
            border: (theme) => `1px solid ${theme.palette.info.surface}`,
            backgroundColor: 'info.surface',
          }}
        >
          {candidates.map((candidate) => {
            const isPicked = picked.includes(candidate.id);
            const meta = getReportStatusMeta(candidate.status);
            const blocked = blockedReason(candidate, direction);

            return (
              <Stack
                key={candidate.id}
                direction='row'
                alignItems={compact ? 'flex-start' : 'center'}
                gap={1.25}
                onClick={() => toggle(candidate.id, !!blocked)}
                sx={{
                  px: 1,
                  py: 1.125,
                  borderRadius: 0.75,
                  cursor: blocked ? 'default' : 'pointer',
                  // Nevyberateľný kandidát sa neskrýva: počet nad zoznamom
                  // hovorí, koľko ich systém našiel, a chýbajúci riadok by
                  // vyzeral ako chyba. Zošedne a povie prečo.
                  opacity: blocked ? 0.55 : 1,
                  backgroundColor: isPicked
                    ? 'background.paper'
                    : 'transparent',
                }}
              >
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    flex: 'none',
                    mt: compact ? 0.25 : 0,
                    display: 'grid',
                    placeItems: 'center',
                    // Kruh = jeden originál, štvorec = viac duplikátov. Tvar
                    // hovorí, koľko sa dá vybrať, skôr než to používateľ skúsi.
                    borderRadius: single ? '999px' : 0.5,
                    border: (theme) =>
                      isPicked
                        ? 'none'
                        : `1px solid ${theme.palette.info.dark}`,
                    backgroundColor: isPicked
                      ? 'primary.main'
                      : 'background.paper',
                    color: 'common.white',
                  }}
                >
                  {isPicked &&
                    (single ? (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '999px',
                          backgroundColor: 'common.white',
                        }}
                      />
                    ) : (
                      <CheckIcon sx={{ fontSize: 13 }} />
                    ))}
                </Box>

                <Stack
                  gap={0.25}
                  sx={{ flex: 1, minWidth: 0 }}
                  direction={compact ? 'column' : 'column'}
                >
                  <Stack
                    direction={compact ? 'column' : 'row'}
                    alignItems={compact ? 'flex-start' : 'center'}
                    gap={compact ? 0.25 : 1.25}
                  >
                    {compact ? (
                      <>
                        <Typography variant='body2' fontWeight={500}>
                          {candidate.category_name ?? candidate.category_id}
                        </Typography>
                        <Typography
                          variant='caption'
                          color='text.secondary'
                          sx={{ fontFamily: 'ui-monospace, Menlo, monospace' }}
                        >
                          {candidate.id}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography
                          variant='caption'
                          color='text.secondary'
                          sx={{ fontFamily: 'ui-monospace, Menlo, monospace' }}
                        >
                          {candidate.id}
                        </Typography>
                        <Typography variant='body2' fontWeight={500}>
                          {candidate.category_name ?? candidate.category_id}
                        </Typography>
                      </>
                    )}
                    <StatusChip tone={meta.tone}>{meta.title}</StatusChip>
                  </Stack>
                  <Typography variant='caption' color='text.secondary'>
                    {candidateMeta(candidate)}
                  </Typography>
                  {blocked && (
                    <Typography variant='caption' color='error.dark'>
                      {blocked}
                    </Typography>
                  )}
                  {/* Reťaz duplicity sa neodmieta, skracuje sa - API zavesí
                      nález rovno na koreň. Úradník má vedieť, kam mu to
                      naozaj pôjde. */}
                  {direction === 'child' &&
                    candidate.status === 'duplicate' && (
                      <Typography variant='caption' color='text.secondary'>
                        {__('zlúči sa do jeho originálu')}
                      </Typography>
                    )}
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      )}

      {direction !== 'none' && (
        <Typography variant='caption' color='text.secondary'>
          {single
            ? __(
                'Tento nález sa uzavrie ako duplikát a preberie riešenie aj vyjadrenie originálu - aj toho, ktorý úrad ešte neprijal. Ostatné návrhy zostanú nedotknuté.',
              )
            : __(
                'Vybrané nálezy sa uzavrú ako duplikáty tohto nálezu a ich autori dostanú vyjadrenie, ktoré napíšete tu.',
              )}
        </Typography>
      )}
    </Stack>
  );
};

export default IssueDuplicates;
